import { Fragment, ReactNode, useMemo } from 'react';

import {
  Container,
  Flex,
  HeadCellSortState,
  Headings,
  Link,
  ResizeAfterLoadingDiv,
  Text,
} from '@foradevelopment/fora-ui';
import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { ActionablePayMonth, EmploymentReportHeadViewModel } from '@fora-monorepo/employer-my-pages-common/types';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { elementLinks, links } from '../../router/links';
import {
  DashboardGridSectionContentFirst,
  DashboardGridSectionContentSecond,
  DashboardGridSectionWrapper,
  DashboardSectionError,
  DashboardSectionLoading,
} from '../components/DashboardGridSection';
import {
  DownloadButtonDeviation,
  DownloadButtonInformation,
  FileDashBoardFirstVisitMessage,
  ReportingActionBox,
  ReportingActionBoxSubSection,
  ReportingStatusOverview,
} from '../../components';
import { FileReportingSection } from '../components/FileReportingSection';
import { sortPayMonthsListIndices } from '../../common/utils/payReportsSorting';
import { getFileDashboardSingle } from '../../redux/fileDashboard/fileDashboardThunks';
import { useTypedTranslation } from '../../i18n/i18n';

export const FileDashboardViewSingle = () => {
  const dispatch = useAppDispatch();
  const employerHasEmployments = useAppSelector(state => state.employment.employerHasEmployments);
  const payMonthOverviewsForYear = useAppSelector(
    state => state.fileDashboard.fileDashboardSingle.payMonthOverviewsForYear
  );
  const latestProcessedReportHasDeviationsAndNoPayrollData = useAppSelector(
    state =>
      state.fileDashboard.fileDashboardSingle.employmentReportStatus.latestProcessedReportHasDeviationsAndNoPayrollData
  );
  const latestProcessedReportHasInformationAndNoPayrollData = useAppSelector(
    state =>
      state.fileDashboard.fileDashboardSingle.employmentReportStatus.latestProcessedReportHasInformationAndNoPayrollData
  );
  const hasPayrollReports = payMonthOverviewsForYear.some(
    payMonthOverView => payMonthOverView.payrollReportHeads.length > 0
  );

  const hasReportedData =
    employerHasEmployments ||
    hasPayrollReports ||
    latestProcessedReportHasDeviationsAndNoPayrollData !== null ||
    latestProcessedReportHasInformationAndNoPayrollData !== null;

  const reloadReportData = () => dispatch(getFileDashboardSingle());

  return (
    <>
      <DataLoader>{hasReportedData ? <DashboardContent /> : <NoReportedDataInfo />}</DataLoader>
      <div id={elementLinks.fileUpload}>
        <FileReportingSection reloadReportData={reloadReportData} />
      </div>
    </>
  );
};

const NoReportedDataInfo = () => (
  <Container width="100%" paddingLeft={[20, 80, 140]} paddingRight={[20, 80, 140]} paddingBottom={40}>
    <Container maxWidth={['100%', '100%', 498]}>
      <FileDashBoardFirstVisitMessage />
    </Container>
  </Container>
);

const DashboardContent = () => {
  const payMonthOverviewsForYear = useAppSelector(
    state => state.fileDashboard.fileDashboardSingle.payMonthOverviewsForYear
  );

  const sortedPayMonthIndices = useMemo(() => {
    return sortPayMonthsListIndices({
      payMonthList: payMonthOverviewsForYear,
      sortState: { reportingPeriods: HeadCellSortState.Desc },
    });
  }, [payMonthOverviewsForYear]);

  return (
    <DashboardGridSectionWrapper>
      <DashboardGridSectionContentFirst>
        <ReportingActionBoxSingle />
      </DashboardGridSectionContentFirst>
      <DashboardGridSectionContentSecond>
        <ReportingStatusOverview data={payMonthOverviewsForYear} sortedIndices={sortedPayMonthIndices} />
        <Flex columnGap={20}>
          <Link to={links.reportHistory}>Visa alla rapporteringshändelser</Link>
          <Link to={links.payHistory}>Visa registrerade löner</Link>
        </Flex>
      </DashboardGridSectionContentSecond>
    </DashboardGridSectionWrapper>
  );
};

const ReportingActionBoxSingle = () => {
  const { t } = useTypedTranslation();
  const { organizationName, organizationNumber, partnerNumber } = useAppSelector(state => state.employer.employerInfo);
  const {
    expiringFaGroups,
    unreportedPayMonths,
    payMonthsWithDeviations,
    payMonthsWithInformation,
    employmentReportWithDeviations,
    employmentReportWithInformation,
  } = useAppSelector(state => state.fileDashboard.fileDashboardSingle);

  if (!unreportedPayMonths && !payMonthsWithDeviations && !expiringFaGroups && !employmentReportWithDeviations) {
    return (
      <ReportingActionBox variant="approved">
        <Text>{t('section.employerActions.descriptionAllDone')}</Text>
        {(payMonthsWithInformation || employmentReportWithInformation) && (
          <InformationSubSection
            payMonthsWithInformation={payMonthsWithInformation}
            employmentReportWithInformation={employmentReportWithInformation}
          />
        )}
      </ReportingActionBox>
    );
  }

  return (
    <ReportingActionBox variant="action">
      {unreportedPayMonths && (
        <ReportingActionBoxSubSection>
          <Headings.H5> {t('section.employerActions.headingRemainingReports-1')} </Headings.H5>
          {unreportedPayMonths.map(payMonth => (
            <Text key={payMonth}>
              {t('section.employerActions.reportingPeriod-1', { payMonth: getReadablePayMonth(payMonth) })}
            </Text>
          ))}
        </ReportingActionBoxSubSection>
      )}
      {(payMonthsWithDeviations || employmentReportWithDeviations) && (
        <ReportingActionBoxSubSection>
          <Headings.H5>{t('section.employerActions.headingDeviation-1')} </Headings.H5>

          {payMonthsWithDeviations?.map(report => (
            <Fragment key={report.payMonth}>
              <Text>
                {t('section.employerActions.reportingPeriod-1', { payMonth: getReadablePayMonth(report.payMonth) })}
              </Text>
              <Container>
                <DownloadButtonDeviation
                  reportId={report.reportId}
                  createdDate={report.createdDate}
                  partnerNumberOrganization={partnerNumber}
                  organizationNumber={organizationNumber}
                  employerName={organizationName}
                />
              </Container>
            </Fragment>
          ))}

          {employmentReportWithDeviations && (
            <>
              <Text>{t('section.employerActions.employeeDetails')}</Text>
              <Container>
                <DownloadButtonDeviation
                  reportId={employmentReportWithDeviations.payrollReportId}
                  createdDate={employmentReportWithDeviations.createdDate}
                  partnerNumberOrganization={partnerNumber}
                  organizationNumber={organizationNumber}
                  employerName={organizationName}
                />
              </Container>
            </>
          )}
        </ReportingActionBoxSubSection>
      )}
      {(payMonthsWithInformation || employmentReportWithInformation) && (
        <InformationSubSection
          payMonthsWithInformation={payMonthsWithInformation}
          employmentReportWithInformation={employmentReportWithInformation}
        />
      )}
      {expiringFaGroups && (
        <ReportingActionBoxSubSection>
          <Headings.H5>{t('section.employerActions.headingNewMarkup-1')}</Headings.H5>
          {expiringFaGroups.map(faGroup => (
            <Text key={faGroup.fokId}>
              {t('section.employerActions.markupExpiring', { fokCode: faGroup.fokId, fokIsExpiring: faGroup.validTo })}
            </Text>
          ))}
          <Link to={links.markups}>{t('warningMessages.expiringFoksLink')}</Link>
        </ReportingActionBoxSubSection>
      )}
    </ReportingActionBox>
  );
};

const InformationSubSection = ({
  payMonthsWithInformation,
  employmentReportWithInformation,
}: {
  payMonthsWithInformation: ActionablePayMonth[] | null;
  employmentReportWithInformation: EmploymentReportHeadViewModel | null;
}) => {
  const { t } = useTypedTranslation();
  const { organizationName, organizationNumber, partnerNumber } = useAppSelector(state => state.employer.employerInfo);
  return (
    <ReportingActionBoxSubSection>
      <Headings.H5>{t('section.employerActions.headingInformationReport')}</Headings.H5>

      {payMonthsWithInformation?.map(report => (
        <Fragment key={report.payMonth}>
          <Text>
            {t('section.employerActions.reportingPeriod-1', { payMonth: getReadablePayMonth(report.payMonth) })}
          </Text>
          <Container>
            <DownloadButtonInformation
              reportId={report.reportId}
              createdDate={report.createdDate}
              partnerNumberOrganization={partnerNumber}
              organizationNumber={organizationNumber}
              employerName={organizationName}
            />
          </Container>
        </Fragment>
      ))}

      {employmentReportWithInformation && (
        <>
          <Text>{t('section.employerActions.employeeDetails')}</Text>
          <Container>
            <DownloadButtonInformation
              reportId={employmentReportWithInformation.payrollReportId}
              createdDate={employmentReportWithInformation.createdDate}
              partnerNumberOrganization={partnerNumber}
              organizationNumber={organizationNumber}
              employerName={organizationName}
            />
          </Container>
        </>
      )}
    </ReportingActionBoxSubSection>
  );
};

const DataLoader = ({ children }: { children: ReactNode }) => {
  const loadingFileDashboardSingle = useAppSelector(state => state.fileDashboard.loadingFileDashboardSingle);
  const { loadingEmployerHasEmployments } = useAppSelector(state => state.employment);

  const loading =
    loadingFileDashboardSingle === ThunkRequestStatus.Pending ||
    loadingEmployerHasEmployments === ThunkRequestStatus.Pending;

  const error =
    loadingFileDashboardSingle === ThunkRequestStatus.Rejected ||
    loadingEmployerHasEmployments === ThunkRequestStatus.Rejected;

  return (
    <ResizeAfterLoadingDiv initialHeight={500} isLoading={loading}>
      {loading ? <DashboardSectionLoading /> : error ? <DashboardSectionError /> : children}
    </ResizeAfterLoadingDiv>
  );
};
